/**
 * 小部件文件预览跳转交接。
 *
 * 小部件被宿主页面以固定尺寸的 iframe 嵌入（plugin.js 写死 458px 宽），
 * 弹窗预览受限于 iframe 宽度，文档缩到 ~426px 后无法阅读。因此小部件下
 * 改为在新标签页打开独立预览页 /preview/:fileId。
 *
 * 交接方式为 postMessage 握手，不用 localStorage、也不进 URL：
 * - localStorage 不可行：小部件是 gbase.ai 嵌在客户站点上的 iframe，
 *   Chrome 115+/Safari 的存储分区会把它的 localStorage 隔离到
 *   (顶级站点, iframe 源) 的分区桶，而新标签页读的是未分区桶，两边不通。
 * - URL 不可行：引用片段是客户文档正文，进 query string 会落到
 *   浏览器历史、referrer 与服务端日志里。
 *
 * 握手时序：opener 开新标签页 -> 新页加载后向 opener 发 ready ->
 * opener 回发 payload。页码额外放在 URL 上作兜底（页码本身不敏感），
 * 握手超时也至少能翻到引用所在页。
 */

const READY = 'gbase-file-preview-ready';
const PAYLOAD = 'gbase-file-preview-payload';
/** 新页加载超时后放弃握手，退回 URL 上的页码 */
const HANDSHAKE_TIMEOUT_MS = 20_000;

export type FilePreviewHandoff =
  | {
      kind: 'pdf';
      file_id: string;
      page: number;
      content: string;
      citationOrder: number;
      title?: string;
    }
  | {
      kind: 'excel';
      file_id: string;
      rows: number[];
      sheet: string;
      title?: string;
    }
  | {
      /** 回答正文文件卡片：走 FileViewer 纯预览，title 是文件名（判格式用） */
      kind: 'file';
      file_id: string;
      page: number;
      title?: string;
    };

const createKey = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/**
 * 打开预览标签页，并等待其索取引用信息。
 * 必须在用户点击的同步调用栈里执行，否则 window.open 会被浏览器拦截。
 *
 * 注意不能加 noopener：新页需要通过 window.opener 发起握手。
 * 目标同源且由我们自己控制，这里放开是安全的。
 */
export const openFilePreviewTab = (payload: FilePreviewHandoff) => {
  const key = createKey();
  const origin = window.location.origin;

  const params = new URLSearchParams({ k: key });
  if ((payload.kind === 'pdf' || payload.kind === 'file') && payload.page) {
    params.set('page', String(payload.page));
  }
  // 文件卡片的新页没有握手结果时也得知道走哪个预览器（页码为 0 时上面不会写 page，单独标记）
  if (payload.kind === 'file') params.set('v', 'file');

  const target = window.open(`/preview/${payload.file_id}?${params.toString()}`, '_blank');
  if (!target) return; // 被拦截

  let timer: ReturnType<typeof setTimeout>;

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== origin) return;
    if (event.data?.type !== READY || event.data?.key !== key) return;

    // 回发给 ready 的来源本身，比持有的 window 句柄更可靠
    (event.source as Window | null)?.postMessage({ type: PAYLOAD, key, payload }, origin);
    // 刻意不在这里摘监听：新页在 StrictMode 下会 setup/cleanup/setup，
    // 第二次 setup 会重发 ready，这里必须还能应答，否则高亮时有时无。
  };

  const cleanup = () => {
    clearTimeout(timer);
    window.removeEventListener('message', onMessage);
  };

  window.addEventListener('message', onMessage);
  timer = setTimeout(cleanup, HANDSHAKE_TIMEOUT_MS);
};

/**
 * 新页侧：向 opener 索取引用信息。
 * 拿不到（无 opener、超时、被刷新）时以 null 回调，由调用方退回页码兜底。
 * 返回清理函数。
 */
export const requestFilePreviewHandoff = (
  key: string | null,
  onSettled: (payload: FilePreviewHandoff | null) => void
) => {
  const origin = window.location.origin;
  const opener = window.opener as Window | null;

  if (!key || !opener) {
    onSettled(null);
    return () => undefined;
  }

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== origin) return;
    if (event.data?.type !== PAYLOAD || event.data?.key !== key) return;
    cleanup();
    onSettled(event.data.payload as FilePreviewHandoff);
  };

  const timer = setTimeout(() => {
    cleanup();
    onSettled(null);
  }, HANDSHAKE_TIMEOUT_MS);

  const cleanup = () => {
    clearTimeout(timer);
    window.removeEventListener('message', onMessage);
  };

  window.addEventListener('message', onMessage);

  try {
    opener.postMessage({ type: READY, key }, origin);
  } catch {
    cleanup();
    onSettled(null); // opener 已关闭或跨源不可达
  }

  return cleanup;
};
